/**
 * Shared safety helpers for AI-surfaced navigation widgets.
 *
 * The assistant emits routes that originate (indirectly) from an LLM, so
 * every CTA that renders a `[routerLink]` must guard against a prompt-
 * injected external URL turning into a styled phishing link. These two
 * pure functions are the single source of truth for that guard — reused
 * by `navigate-action`, `alert` (route mode) and `entity-list` (see-all).
 */

/**
 * Same guard as `safeInAppRoute`, but returns null instead of "/" when
 * the route is unusable — for callers that want to fall back to their
 * own default (or render a non-clickable row) rather than link home.
 */
export function inAppRouteOrNull(route: string | null | undefined): string | null {
  if (typeof route !== 'string' || !route.startsWith('/')) {
    return null;
  }
  return route;
}

/**
 * Reject anything that isn't an in-app path. Only routes starting with
 * "/" are allowed through; everything else (external URLs, empty, null,
 * non-strings) falls back to the cockpit root "/".
 */
export function safeInAppRoute(route: string | null | undefined): string {
  return inAppRouteOrNull(route) ?? '/';
}

/**
 * Coerce a query-param bag to string values RouterLink accepts, dropping
 * null/undefined entries. Returns null when nothing usable remains so
 * the template can bind `[queryParams]="null"` (a no-op).
 */
export function normalizeQueryParams(
  raw: Record<string, string | number | boolean> | null | undefined,
): Record<string, string> | null {
  if (!raw) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v === null || v === undefined) continue;
    out[k] = String(v);
  }
  return Object.keys(out).length > 0 ? out : null;
}
